import { useState, useEffect } from 'react';

type DeviceType = 'mobile' | 'tablet' | 'desktop';

export const useDeviceDetection = () => {
  const [deviceType, setDeviceType] = useState<DeviceType>('desktop');

  const detectDevice = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    const width = window.innerWidth;
    const height = window.innerHeight;
    
    // Verificar si hay una preferencia guardada en localStorage
    const savedDevicePreference = localStorage.getItem('preferredDeviceType') as DeviceType | null;
    if (savedDevicePreference && ['mobile', 'tablet', 'desktop'].includes(savedDevicePreference)) {
      setDeviceType(savedDevicePreference);
      return;
    }
    
    // Verficiar parámetros de URL para forzar un tipo de dispositivo (para propósitos de prueba)
    const urlParams = new URLSearchParams(window.location.search);
    const deviceParam = urlParams.get('device') as DeviceType | null;
    if (deviceParam && ['mobile', 'tablet', 'desktop'].includes(deviceParam)) {
      setDeviceType(deviceParam);
      return;
    }
    
    // Detectar si es un dispositivo Android (incluyendo terminales de pago)
    const isAndroid = /android/i.test(userAgent);
    
    // Detectar si es iOS
    const isIOS = /ipad|iphone|ipod/i.test(userAgent);
    
    // Detectar si es tablet basado en user agent
    const isTabletByUA = 
      /ipad|tablet|(android(?!.*mobile))/i.test(userAgent) ||
      // Detectar tablets Samsung
      /sm-t|galaxy tab/i.test(userAgent) ||
      // Detectar tablets Huawei y otras marcas comunes
      /mediapad|matepad|pad pro|mi pad|zenpad/i.test(userAgent);
    
    // Detectar si es tablet basado en proporción de pantalla y tamaño
    // Muchas tablets tienen proporción cercana a 4:3 (1.33) mientras que los teléfonos suelen ser más alargados
    const aspectRatio = width / height;
    const isTabletBySize = 
      // Tablets de mayor tamaño pueden tener resoluciones superiores a 1024px
      (width >= 600 && width <= 1366) && 
      // Tablets típicamente tienen aspect ratio entre 1.3 y 1.7
      (aspectRatio >= 0.6 && aspectRatio <= 1.8);
    
    // Detectar si es móvil basado en user agent o tamaño de pantalla
    const isMobileByUA = 
      /iphone|ipod|android.*mobile|windows.*phone|blackberry/i.test(userAgent);
    
    const isMobileBySize = width < 600;
    
    // Detección para terminales de pago Android (suelen tener Android en su UA)
    const isPOS = isAndroid && /pos|point[-_]of[-_]sale|terminal[-_]de[-_]pago/i.test(userAgent);

    // Detectar tablet de 10 pulgadas (común en retail y POS)
    const is10InchTablet = 
      (width >= 1200 && width <= 1366 && height >= 800 && height <= 1024) || 
      (height >= 1200 && height <= 1366 && width >= 800 && width <= 1024);

    if (isPOS) {
      // Terminales de pago siempre se consideran como tablet para usar interfaz paso a paso
      setDeviceType('tablet');
    } else if (is10InchTablet || isTabletByUA || isTabletBySize) {
      // Tablets de 10 pulgadas y otras tablets detectadas
      setDeviceType('tablet');
    } else if (isMobileByUA || isMobileBySize) {
      setDeviceType('mobile');
    } else if (isAndroid || isIOS) {
      // Consideramos todos los dispositivos Android e iOS no clasificados como teléfonos como tablets
      setDeviceType('tablet');
    } else {
      setDeviceType('desktop');
    }
  };

  useEffect(() => {
    // Detectar dispositivo al cargar la página
    detectDevice();
    
    // Actualizar cuando cambie el tamaño de la ventana
    window.addEventListener('resize', detectDevice);
    
    // Limpiar el event listener
    return () => {
      window.removeEventListener('resize', detectDevice);
    };
  }, []);

  // Los dispositivos móviles y tablets (incluidos terminales de pago) necesitan la interfaz paso a paso
  // Solo los desktops utilizan la interfaz normal
  const needsStepByStepInterface = deviceType === 'mobile' || deviceType === 'tablet';

  // Función para forzar un tipo de dispositivo (útil para pruebas)
  const setForceDeviceType = (type: DeviceType, persist: boolean = false) => {
    setDeviceType(type);
    if (persist) {
      localStorage.setItem('preferredDeviceType', type);
    }
  };

  return {
    deviceType,
    isMobile: deviceType === 'mobile',
    isTablet: deviceType === 'tablet',
    isDesktop: deviceType === 'desktop',
    needsStepByStepInterface,
    setForceDeviceType
  };
};
